/**
 * GameContainer.jsx
 * 
 * Phase 1: Foundation component that replaces the canvas element.
 * Provides a 3-layer structure for DOM/SVG-based game rendering.
 * 
 * Layers:
 * - Background: Parallax-ready stage background
 * - World: Camera-transformed container for fighters and world objects
 * - Overlay: HUD, combo text, effects (not camera-transformed)
 */

import React, { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';

export const GameContainer = forwardRef(({
  width = 1280,
  height = 720,
  children,
  overlayChildren,
  backgroundComponent: BackgroundComponent,
  backgroundProps = {},
}, ref) => {
  const containerRef = useRef(null);
  const worldRef = useRef(null);
  const hudRef = useRef(null);
  const effectsRef = useRef(null);

  // Expose refs and methods to parent
  useImperativeHandle(ref, () => ({
    container: containerRef.current,
    world: worldRef.current,
    hud: hudRef.current,
    effects: effectsRef.current,
    
    /**
     * Update camera transform - called from RAF loop for 60fps positioning
     * Uses CSS custom properties for GPU-accelerated transforms
     */
    setCamera: (x, y, zoom = 1) => {
      if (worldRef.current) {
        worldRef.current.style.setProperty('--camera-x', `${-x}px`);
        worldRef.current.style.setProperty('--camera-y', `${-y}px`);
        worldRef.current.style.setProperty('--camera-zoom', zoom);
      }
    },

    /**
     * Trigger screen shake effect
     * @param {number} intensity - Shake intensity in pixels (0-20)
     * @param {number} duration - Duration in ms
     */
    shake: (intensity = 8, duration = 200) => {
      if (!containerRef.current) return;
      
      const startTime = performance.now();
      const shake = (now) => {
        const elapsed = now - startTime;
        if (elapsed >= duration) {
          containerRef.current.style.setProperty('--shake-x', '0px');
          containerRef.current.style.setProperty('--shake-y', '0px');
          return;
        }
        
        const progress = elapsed / duration;
        const dampening = 1 - progress;
        const currentIntensity = intensity * dampening;
        
        const shakeX = (Math.random() - 0.5) * 2 * currentIntensity;
        const shakeY = (Math.random() - 0.5) * 2 * currentIntensity;
        
        containerRef.current.style.setProperty('--shake-x', `${shakeX}px`);
        containerRef.current.style.setProperty('--shake-y', `${shakeY}px`);
        
        requestAnimationFrame(shake);
      };
      requestAnimationFrame(shake);
    },

    /**
     * Get bounding client rect for hit detection
     */
    getBoundingClientRect: () => {
      return containerRef.current?.getBoundingClientRect();
    },
  }));

  return (
    <div
      ref={containerRef}
      className="game-container"
      style={{
        '--game-width': `${width}px`,
        '--game-height': `${height}px`,
        '--camera-x': '0px',
        '--camera-y': '0px',
        '--camera-zoom': '1',
        '--shake-x': '0px',
        '--shake-y': '0px',
      }}
    >
      {/* Layer 1: Background - Parallax-ready */}
      <div className="game-layer game-layer-background">
        {BackgroundComponent && (
          <BackgroundComponent {...backgroundProps} />
        )}
      </div>

      {/* Layer 2: World - Camera-transformed */}
      <div ref={worldRef} className="game-layer game-layer-world">
        {children}
      </div>

      {/* Layer 3: Overlay - HUD, combo text, effects */}
      <div ref={effectsRef} className="game-layer game-layer-effects">
        {overlayChildren}
      </div>
      <div ref={hudRef} className="game-layer game-layer-hud" />
    </div>
  );
});

GameContainer.displayName = 'GameContainer';

export default GameContainer;
